/** Dialog, popover, tooltip and dropdown menu wrappers styled with tokens (Radix: focus trap, Esc, ARIA). */
import {
  Dialog as DialogPrimitive,
  DropdownMenu as DropdownMenuPrimitive,
  Popover as PopoverPrimitive,
  Tooltip as TooltipPrimitive,
} from 'radix-ui';
import { XIcon } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';
import { useUiTranslation } from '../i18n/ui_i18n';
import { classNames } from '../styling/class_names';

const floatingSurfaceClasses =
  'z-50 rounded-control border border-hair bg-card text-ink shadow-overlay outline-none';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

export interface DialogContentProps extends Omit<ComponentProps<typeof DialogPrimitive.Content>, 'title'> {
  title: ReactNode;
  description?: ReactNode;
  /** Wider dialogs for help lists and previews. */
  size?: 'sm' | 'md' | 'lg' | 'fullscreen';
  hideCloseButton?: boolean;
}

const dialogSizeClasses: Record<NonNullable<DialogContentProps['size']>, string> = {
  sm: 'w-[min(420px,calc(100vw-32px))]',
  md: 'w-[min(560px,calc(100vw-32px))]',
  lg: 'w-[min(880px,calc(100vw-32px))]',
  fullscreen: 'w-[calc(100vw-32px)] h-[calc(100vh-32px)]',
};

export function DialogContent({
  title,
  description,
  size = 'md',
  hideCloseButton = false,
  className,
  children,
  onOpenAutoFocus,
  ...contentProps
}: DialogContentProps) {
  const { t: translate } = useUiTranslation();
  /** A dialog with a form starts in its first field (re-authentication, typed confirmation), not on the close X. */
  const focusFirstField = (focusEvent: Event) => {
    onOpenAutoFocus?.(focusEvent);
    if (focusEvent.defaultPrevented || !(focusEvent.currentTarget instanceof HTMLElement)) {
      return;
    }
    const firstField = focusEvent.currentTarget.querySelector<HTMLElement>(
      'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled])',
    );
    if (firstField !== null) {
      focusEvent.preventDefault();
      firstField.focus();
    }
  };
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-overlay" />
      <DialogPrimitive.Content
        className={classNames(
          'fixed left-1/2 top-1/2 z-50 flex max-h-[calc(100vh-32px)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-card border border-hair bg-card text-ink shadow-overlay outline-none',
          dialogSizeClasses[size],
          className,
        )}
        onOpenAutoFocus={focusFirstField}
        {...contentProps}
      >
        <div className="flex items-start justify-between gap-4 px-5 pt-5">
          <div className="flex flex-col gap-1">
            <DialogPrimitive.Title className="font-display text-lg font-semibold">
              {title}
            </DialogPrimitive.Title>
            {description === undefined ? (
              <DialogPrimitive.Description className="sr-only">{title}</DialogPrimitive.Description>
            ) : (
              <DialogPrimitive.Description className="text-sm text-muted">
                {description}
              </DialogPrimitive.Description>
            )}
          </div>
          {hideCloseButton ? null : (
            <DialogPrimitive.Close
              className="grid size-8 shrink-0 place-items-center rounded-control text-muted hover:bg-neutral-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--focus-ring)]"
              aria-label={translate('close')}
            >
              <XIcon className="size-4" aria-hidden />
            </DialogPrimitive.Close>
          )}
        </div>
        <div className="min-h-0 flex-1 overflow-auto px-5 pb-5 pt-4">{children}</div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

/** Action row at the bottom of a dialog: divider, buttons right-aligned, primary last ([Bekor qilish] [Saqlash]). */
export function DialogFooter({ className, ...footerProps }: ComponentProps<'div'>) {
  return (
    <div
      className={classNames(
        '-mx-5 -mb-5 mt-2 flex flex-wrap items-center justify-end gap-2 border-t border-hair px-5 py-3',
        className,
      )}
      {...footerProps}
    />
  );
}

export const Popover = PopoverPrimitive.Root;
export const PopoverTrigger = PopoverPrimitive.Trigger;
export const PopoverAnchor = PopoverPrimitive.Anchor;

export function PopoverContent({
  className,
  align = 'start',
  sideOffset = 6,
  collisionPadding = 8,
  ...contentProps
}: ComponentProps<typeof PopoverPrimitive.Content>) {
  return (
    <PopoverPrimitive.Portal>
      {/* Flips above the trigger when there is more room there and keeps an 8 px gap to the viewport edges. */}
      <PopoverPrimitive.Content
        align={align}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        className={classNames(floatingSurfaceClasses, 'p-3', className)}
        {...contentProps}
      />
    </PopoverPrimitive.Portal>
  );
}

export const TooltipProvider = TooltipPrimitive.Provider;

export interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** true keeps the tooltip closed while the trigger stays mounted (e.g. a disabled reason that went away). */
  disabled?: boolean;
}

/** Hover/focus tooltip. The trigger child must be focusable so keyboard users see it too (NFR-08). */
export function Tooltip({ content, children, side = 'top', disabled = false }: TooltipProps) {
  return (
    <TooltipPrimitive.Root delayDuration={200} {...(disabled ? { open: false } : {})}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={6}
          className="z-50 max-w-72 rounded-control bg-ink px-2.5 py-1.5 text-xs text-card shadow-overlay"
        >
          {content}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

export const DropdownMenu = DropdownMenuPrimitive.Root;
export const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
export const DropdownMenuGroup = DropdownMenuPrimitive.Group;

export function DropdownMenuContent({
  className,
  align = 'end',
  sideOffset = 6,
  ...contentProps
}: ComponentProps<typeof DropdownMenuPrimitive.Content>) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        align={align}
        sideOffset={sideOffset}
        className={classNames(floatingSurfaceClasses, 'min-w-44 p-1', className)}
        {...contentProps}
      />
    </DropdownMenuPrimitive.Portal>
  );
}

const menuItemClasses =
  'flex cursor-pointer select-none items-center gap-2 rounded-[6px] px-2.5 py-1.5 text-base outline-none data-[highlighted]:bg-neutral-soft data-[disabled]:opacity-50 [&_svg]:size-4';

export function DropdownMenuItem({
  className,
  ...itemProps
}: ComponentProps<typeof DropdownMenuPrimitive.Item>) {
  return <DropdownMenuPrimitive.Item className={classNames(menuItemClasses, className)} {...itemProps} />;
}

export function DropdownMenuCheckboxItem({
  className,
  children,
  ...itemProps
}: ComponentProps<typeof DropdownMenuPrimitive.CheckboxItem>) {
  return (
    <DropdownMenuPrimitive.CheckboxItem
      className={classNames(menuItemClasses, 'pl-8 relative', className)}
      {...itemProps}
    >
      <span className="absolute left-2.5 grid size-4 place-items-center">
        <DropdownMenuPrimitive.ItemIndicator>✓</DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  );
}

export function DropdownMenuLabel({
  className,
  ...labelProps
}: ComponentProps<typeof DropdownMenuPrimitive.Label>) {
  return (
    <DropdownMenuPrimitive.Label
      className={classNames('pc-eyebrow px-2.5 py-1.5', className)}
      {...labelProps}
    />
  );
}

export function DropdownMenuSeparator({
  className,
  ...separatorProps
}: ComponentProps<typeof DropdownMenuPrimitive.Separator>) {
  return (
    <DropdownMenuPrimitive.Separator
      className={classNames('my-1 h-px bg-hair', className)}
      {...separatorProps}
    />
  );
}
